import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { RefreshCw, X, Search, Filter, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { API_CONFIG } from '../services/config';
import { ALL_BROKERS, BROKER_LABELS, BROKER_BADGE_VARIANT, BROKER_BADGE_TEXT, BrokerName } from '../services/brokerApi';

interface Order {
  id: number;
  order_id: string;
  trading_symbol: string;
  transaction_type: string;
  order_status: string;
  quantity: number;
  price: number;
  filled_qty: number;
  order_type: string;
  product_type: string;
  create_time: string;
  exchange_segment: string;
  validity: string;
  security_id: string;
  broker?: string;
  oms_error_description?: string | null;
  internal_error?: string | null;
}

const BseOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [brokerFilter, setBrokerFilter] = useState('ALL');
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => { filterOrders(); }, [orders, searchTerm, statusFilter, typeFilter, brokerFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/bse/orders?page=1&limit=100`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to fetch BSE orders');
      const data = await response.json();
      setOrders(data.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    if (searchTerm) filtered = filtered.filter(o => o.trading_symbol?.toLowerCase().includes(searchTerm.toLowerCase()) || o.order_id?.includes(searchTerm));
    if (statusFilter !== 'ALL') filtered = filtered.filter(o => o.order_status === statusFilter);
    if (typeFilter !== 'ALL') filtered = filtered.filter(o => o.transaction_type === typeFilter);
    if (brokerFilter !== 'ALL') filtered = filtered.filter(o => (o.broker || 'DHAN') === brokerFilter);
    setFilteredOrders(filtered);
  };

  const handleClearHistory = async () => {
    setShowClearModal(false);
    try {
      await fetch(`${API_CONFIG.baseURL}/bse/orders/clear`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      setOrders([]);
    } catch (err: any) { setError(err.message); }
  };

  const handleDeletePermanently = async () => {
    setShowDeleteModal(false);
    try {
      await fetch(`${API_CONFIG.baseURL}/bse/orders/delete-permanently`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      setOrders([]);
    } catch (err: any) { setError(err.message); }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'TRADED': case 'EXECUTED': return 'success';
      case 'REJECTED': return 'danger';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'secondary';
      default: return 'info';
    }
  };

  const stats = {
    total: orders.length,
    traded: orders.filter(o => o.order_status === 'TRADED' || o.order_status === 'EXECUTED').length,
    pending: orders.filter(o => o.order_status === 'PENDING').length,
    rejected: orders.filter(o => o.order_status === 'REJECTED').length,
  };

  return (
    <MainLayout>
      <Container fluid className="py-3" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
        {/* Header */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1" style={{ fontWeight: '700', fontSize: '22px' }}>
                BSE Order Book
                <span style={{ marginLeft: '10px', background: '#fd7e14', color: 'white', padding: '3px 10px', borderRadius: '4px', fontSize: '13px', fontWeight: '600' }}>BSE_FNO</span>
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>SENSEX options orders — Lot size: 20</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={fetchOrders} disabled={loading}>
                <RefreshCw size={14} className="me-1" />Refresh
              </Button>
              <Button variant="warning" size="sm" onClick={() => setShowClearModal(true)} disabled={orders.length === 0}>
                <X size={14} className="me-1" />Clear History
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)} disabled={orders.length === 0}>
                <X size={14} className="me-1" />Delete Permanently
              </Button>
            </div>
          </div>

          {/* Stats */}
          <Row className="g-2 mb-3">
            {[
              { label: 'Total', value: stats.total, color: '#212529' },
              { label: 'Traded', value: stats.traded, color: '#28a745' },
              { label: 'Pending', value: stats.pending, color: '#ffc107' },
              { label: 'Rejected', value: stats.rejected, color: '#dc3545' },
            ].map(s => (
              <Col md={2} key={s.label}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-3">
                    <div className="text-muted" style={{ fontSize: '12px' }}>{s.label}</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.value}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {error && <div className="alert alert-danger mb-3">{error}</div>}

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="p-3">
            <Row className="g-2">
              <Col md={4}>
                <InputGroup size="sm">
                  <InputGroup.Text><Search size={14} /></InputGroup.Text>
                  <Form.Control placeholder="Search symbol or order ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ fontSize: '13px' }} />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: '13px' }}>
                  <option value="ALL">All Status</option>
                  <option value="TRADED">Traded</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="CANCELLED">Cancelled</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ fontSize: '13px' }}>
                  <option value="ALL">All Types</option>
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select size="sm" value={brokerFilter} onChange={e => setBrokerFilter(e.target.value)} style={{ fontSize: '13px' }}>
                  <option value="ALL">All Brokers</option>
                  {ALL_BROKERS.map(b => (
                    <option key={b} value={b}>{BROKER_LABELS[b]}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
              <thead style={{ background: '#fff3e0', borderBottom: '2px solid #ffcc80' }}>
                <tr>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>TIME</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>SYMBOL</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>TYPE</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>STATUS</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>QTY</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>PRICE</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>ORDER TYPE</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>SECURITY ID</th>
                  <th style={{ padding: '10px', fontSize: '11px', fontWeight: '600' }}>BROKER</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-5"><Spinner animation="border" size="sm" className="me-2" />Loading BSE orders...</td></tr>
                ) : filteredOrders.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-5 text-muted"><AlertCircle size={32} className="mb-2 d-block mx-auto" style={{ opacity: 0.4 }} />No BSE orders found</td></tr>
                ) : (
                  filteredOrders.map(order => (
                    <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px', color: '#6c757d' }}>
                        {order.create_time ? new Date(order.create_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ fontWeight: '600' }}>{order.trading_symbol}</div>
                        <div style={{ fontSize: '11px', color: '#fd7e14' }}>{order.exchange_segment}</div>
                      </td>
                      <td style={{ padding: '10px', fontWeight: '600', color: order.transaction_type === 'BUY' ? '#28a745' : '#dc3545' }}>
                        {order.transaction_type}
                      </td>
                      <td style={{ padding: '10px', maxWidth: '220px' }}>
                        <Badge bg={getStatusVariant(order.order_status)} style={{ fontSize: '11px' }}>{order.order_status}</Badge>
                        {order.order_status === 'REJECTED' && (order.oms_error_description || order.internal_error) && (
                          <div
                            title={order.oms_error_description || order.internal_error || ''}
                            style={{
                              fontSize: '10px', color: '#dc3545', marginTop: '3px',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help'
                            }}
                          >
                            {order.oms_error_description || order.internal_error}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>{order.quantity}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>₹{Number(order.price).toFixed(2)}</td>
                      <td style={{ padding: '10px', color: '#6c757d' }}>{order.order_type}</td>
                      <td style={{ padding: '10px', color: '#6c757d' }}>{order.security_id}</td>
                      <td style={{ padding: '10px' }}>
                        <Badge bg={BROKER_BADGE_VARIANT[(order.broker || 'DHAN') as BrokerName]} text={BROKER_BADGE_TEXT[(order.broker || 'DHAN') as BrokerName]} style={{ fontSize: '10px' }}>
                          {order.broker || 'DHAN'}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {filteredOrders.length > 0 && (
          <div className="text-center mt-3 text-muted" style={{ fontSize: '13px' }}>
            Showing {filteredOrders.length} of {orders.length} BSE orders
          </div>
        )}
      </Container>

      {/* Clear Modal */}
      {showClearModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '480px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffc107', marginBottom: '8px' }}>Clear BSE Order History?</h3>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px' }}>This will hide {orders.length} BSE orders from view (soft delete).</p>
            <div className="d-flex gap-2 justify-content-end">
              <button onClick={() => setShowClearModal(false)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleClearHistory} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#ffc107', color: 'white', cursor: 'pointer' }}>Clear History</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '480px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#dc3545', marginBottom: '8px' }}>Delete BSE Orders Permanently?</h3>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px' }}>This will permanently delete {orders.length} BSE orders. Cannot be undone!</p>
            <div className="d-flex gap-2 justify-content-end">
              <button onClick={() => setShowDeleteModal(false)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeletePermanently} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#dc3545', color: 'white', cursor: 'pointer' }}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default BseOrdersPage;
